import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronDown, Menu, X, Phone } from 'lucide-react';

// G3 architecture navigation: Residential / Commercial / Systems / Industries / Locations / Projects / About
const menus = [
  {
    key: 'residential',
    label: 'Residential',
    items: [
      { name: 'All Residential Coatings', path: '/residential-concrete-coatings' },
      { name: 'Garage Floor Coating', path: '/garage-floor-coating' },
      { name: 'Flake Garage Floors', path: '/garage-flake-floors-naples-fl' },
      { name: 'Pool Deck Resurfacing', path: '/pool-deck-resurfacing' },
      { name: 'Driveway Resurfacing', path: '/driveway-resurfacing' },
      { name: 'Interior Metallic Floors', path: '/metallic-epoxy-floors-naples-fl' },
      { name: 'Polished Concrete', path: '/concrete-polishing-naples-fl' },
    ],
  },
  {
    key: 'commercial',
    label: 'Commercial',
    items: [
      { name: 'All Commercial Coatings', path: '/commercial-floor-coatings' },
      { name: 'Commercial Epoxy Naples', path: '/commercial-epoxy-flooring-naples-fl' },
      { name: 'Commercial Kitchens', path: '/commercial-kitchen-flooring-naples-fl' },
      { name: 'Industrial & Warehouse', path: '/industrial-epoxy' },
      { name: 'Aircraft Hangars', path: '/airplane-hangar-floor-coatings-florida' },
      { name: 'HOA & Condo Coatings', path: '/hoa-condo-floor-coatings-sarasota-manatee' },
      { name: 'Balcony Waterproofing', path: '/condo-balcony-waterproofing-naples-fl' },
      { name: 'Coating Removal & Prep', path: '/concrete-coating-removal-naples-fl' },
      { name: 'Government Contracts', path: '/government-contracts' },
    ],
  },
  {
    key: 'systems',
    label: 'Systems',
    items: [
      { name: 'All Flooring Systems', path: '/concrete-flooring-systems' },
      { name: 'Decorative Flake', path: '/garage-flake-floors-naples-fl' },
      { name: 'Polyaspartic Floor Coating', path: '/polyaspartic-floor-coating-naples-fl' },
      { name: 'Metallic Epoxy', path: '/metallic-epoxy-floors-naples-fl' },
      { name: 'Quartz Systems', path: '/quartz-pool-deck-coatings-naples-fl' },
      { name: 'Urethane Cement', path: '/urethane-cement-flooring-naples-fl' },
      { name: 'Polished Concrete', path: '/concrete-polishing-naples-fl' },
      { name: 'Concrete Repair', path: '/concrete-repair' },
      { name: 'Waterproofing (TREMCO)', path: '/waterproofing' },
    ],
  },
  {
    key: 'locations',
    label: 'Naples Areas',
    items: [
      { name: 'Epoxy Flooring Naples', path: '/epoxy-flooring-naples' },
      { name: 'Garage Floor Coating Naples', path: '/garage-floor-coating' },
      { name: 'Pool Deck Resurfacing Naples', path: '/pool-deck-resurfacing' },
      { name: 'Marco Island', path: '/epoxy-flooring-marco-island' },
      { name: 'Bonita Springs', path: '/epoxy-flooring-bonita-springs' },
      { name: 'Estero', path: '/epoxy-flooring-estero' },
      { name: 'Fort Myers, Cape Coral & SWFL →', path: '/service-areas' },
    ],
  },
  {
    key: 'projects',
    label: 'Projects',
    items: [
      { name: 'Case Studies', path: '/case-studies' },
      { name: 'Project Gallery', path: '/gallery' },
      { name: 'Flake Showroom', path: '/showroom' },
      { name: 'AI Floor Visualizer', path: '/ai-visualizer' },
      { name: 'Floor Visualizer', path: '/visualizer' },
    ],
  },
  {
    key: 'about',
    label: 'About',
    items: [
      { name: 'Why EpoxyArt33', path: '/why-epoxyart33' },
      { name: 'Our Epoxy Floor Installers', path: '/epoxy-floor-installers-naples-fl' },
      { name: '2026 Pricing Guide', path: '/epoxy-floor-installation-cost-naples-fl' },
      { name: 'Warranty', path: '/warranty' },
      { name: 'Products & Materials', path: '/products' },
      { name: 'Blog', path: '/blog' },
      { name: 'FAQ', path: '/faq' },
      { name: 'Contact', path: '/contact' },
    ],
  },
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 w-full bg-brand-black/95 backdrop-blur-sm border-b border-brand-gold/20 z-50 shadow-lg" data-testid="site-navbar">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo - Premium Gold Wordmark (Optimized for LCP) */}
          <Link to="/" className="flex items-center space-x-3" data-testid="navbar-logo-link">
            <picture>
              <source srcSet="/logos/logo-wordmark.avif" type="image/avif" />
              <source srcSet="/logos/logo-wordmark.webp" type="image/webp" />
              <img
                src="/logos/logo-wordmark.png"
                alt="Epoxy Art 33 - Concrete Coatings & Surface Solutions Naples FL"
                width="240"
                height="64"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="h-12 md:h-14 w-auto"
                data-testid="navbar-logo-img"
              />
            </picture>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-5 xl:space-x-6">
            <Link
              to="/"
              className={`font-medium text-[15px] transition-colors ${isActive('/') ? 'text-brand-gold' : 'text-white hover:text-brand-gold'}`}
            >
              Home
            </Link>

            {menus.map((menu) => (
              <div key={menu.key} className="relative group">
                <button
                  className="flex items-center font-medium text-[15px] text-white hover:text-brand-gold transition-colors"
                  aria-label={`Open ${menu.label} menu`}
                  aria-haspopup="true"
                  data-testid={`nav-menu-${menu.key}`}
                >
                  {menu.label} <ChevronDown className="ml-1 w-4 h-4" />
                </button>
                <div className="absolute left-0 mt-2 w-64 bg-brand-black border border-brand-gold/30 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {menu.items.map((item) => (
                    <Link
                      key={item.path + item.name}
                      to={item.path}
                      className="block px-4 py-3 text-gray-300 hover:bg-brand-gold/10 hover:text-brand-gold transition-colors first:rounded-t-lg last:rounded-b-lg"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            <Link
              to="/industries"
              className={`font-medium text-[15px] transition-colors ${isActive('/industries') ? 'text-brand-gold' : 'text-white hover:text-brand-gold'}`}
              data-testid="nav-industries-link"
            >
              Industries
            </Link>

            {/* CTA Buttons */}
            <div className="flex items-center space-x-3">
              <a href="tel:2392761462" className="flex items-center text-brand-gold font-bold hover:text-brand-gold-light">
                <Phone className="w-4 h-4 mr-1" />
                <span className="hidden xl:inline">(239) 276-1462</span>
              </a>
              <Link to="/contact">
                <Button className="bg-brand-gold hover:bg-brand-gold-light text-brand-black font-bold" aria-label="Get an estimate for concrete coatings" data-testid="nav-estimate-btn">
                  Get an Estimate
                </Button>
              </Link>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white hover:text-brand-gold focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-brand-black border-t border-brand-gold/20 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="container mx-auto px-4 py-4 space-y-2">
            <Link
              to="/"
              className="block py-2 text-white hover:text-brand-gold font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>

            {menus.map((menu) => (
              <div key={menu.key}>
                <button
                  className="flex items-center justify-between w-full py-2 text-white hover:text-brand-gold font-medium"
                  onClick={() => setOpenMenu(openMenu === menu.key ? null : menu.key)}
                  aria-label={`${openMenu === menu.key ? 'Close' : 'Open'} ${menu.label} menu`}
                  aria-expanded={openMenu === menu.key}
                  data-testid={`mobile-menu-${menu.key}`}
                >
                  {menu.label}
                  <ChevronDown className={`w-4 h-4 transform transition-transform ${openMenu === menu.key ? 'rotate-180' : ''}`} />
                </button>
                {openMenu === menu.key && (
                  <div className="pl-4 space-y-1 border-l-2 border-brand-gold/30 ml-2">
                    {menu.items.map((item) => (
                      <Link
                        key={item.path + item.name}
                        to={item.path}
                        className="block py-2 text-sm text-gray-400 hover:text-brand-gold"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <Link
              to="/industries"
              className="block py-2 text-white hover:text-brand-gold font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Industries
            </Link>

            <div className="pt-4 space-y-3 border-t border-brand-gold/20">
              <a
                href="tel:2392761462"
                className="flex items-center justify-center py-3 text-brand-gold font-bold text-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                (239) 276-1462
              </a>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-brand-gold hover:bg-brand-gold-light text-brand-black font-bold py-3" aria-label="Get an estimate">
                  Get an Estimate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
